'use client';
import React from 'react';
import styles from './styles.module.scss';
import { FilterOptionKeys, FilterOptions, MenuType, ProductCounters, SearchParams } from '@mybestbrands/types';
import { CategoriesModal } from '@/app/ui/components/filters/mobile/CategoriesModal';
import { selectedFilterCount } from '@/app/helpers/searchparams';
import { Sliders } from 'react-bootstrap-icons';
import { FiltersModal } from '@/app/ui/components/filters/mobile/FiltersModal';
import { useTranslation } from 'next-i18next';
import SaleFilter from '@/app/ui/components/filters/mobile/topfilters/salefilter';
import { getSelectedFilterCount } from '@/app/utils/common';
import { trackSSTEvent } from '@/app/utils/tracking';
import VisibilityTracker from '@/app/ui/components/tracking/visibilityTracker';

interface Props {
  facets: FilterOptions | null;
  searchParams: SearchParams;
  counters: ProductCounters | null;
  disabledFilters?: FilterOptionKeys[];
  categories?: MenuType[] | undefined | null;
  topFilters?: FilterOptionKeys[];
  blackFriday?: boolean | undefined;
}

const MODALS = {
  CATEGORIES: 'categories',
  FILTERS: 'filters',
} as const;

type ModalKey = (typeof MODALS)[keyof typeof MODALS];

const TopFilters = ({
  facets,
  searchParams,
  counters,
  disabledFilters,
  categories,
  topFilters,
  blackFriday,
}: Props) => {
  const [activeModal, setActiveModal] = React.useState<ModalKey | null>(null);
  const isCategoryFilterApplied = !!searchParams?.filters?.categories?.length;
  const filtersCount = selectedFilterCount(searchParams, [FilterOptionKeys.GENDERS]);
  const { t } = useTranslation(['filters']);
  const [activeSubFilter, setActiveSubFilter] = React.useState<string | null>(null);
  const [isTopFilter, setIsTopFilter] = React.useState(false);
  const showSale =
    (!!facets?.sale?.options?.length || !!facets?.sale?.options?.filter((el) => el.id !== 'sale').length) &&
    !searchParams?.filters?.hottest_sale;

  if (!topFilters) return null;

  const openFiltersModal = (subFilter?: string) => {
    setActiveSubFilter(subFilter || '');
    setActiveModal(MODALS.FILTERS);
    setIsTopFilter(true);
    // Track the open event first
    trackSSTEvent('top_filter_open', {
      filter_name: subFilter,
      action: 'open',
    });
  };

  const openModal = (modal: ModalKey) => {
    setActiveModal(modal);
    setIsTopFilter(false);
    if (modal === MODALS.CATEGORIES) {
      // Track the open event for categories
      trackSSTEvent('top_filter_open', {
        filter_name: 'categories',
        action: 'open',
      });
    }
  };
  const closeModal = () => {
    setActiveModal(null);
    setActiveSubFilter(null);
    setIsTopFilter(false);
  };

  return (
    <>
      <div className={styles['horizontalScroll']}>
        <div className={styles['filter']} onClick={() => openModal(MODALS.FILTERS)}>
          <Sliders size={18} />
          {filtersCount > 0 && <span className={`mb-4 ${styles['filterCounter']}`}>{filtersCount}</span>}
        </div>
        {topFilters.map((filter) => {
          if (filter === FilterOptionKeys.SALE) {
            return (
              showSale &&
              (!disabledFilters || (!disabledFilters.includes(FilterOptionKeys.SALE) && <SaleFilter key={filter} />))
            );
          }

          if (filter === FilterOptionKeys.CATEGORIES) {
            return (
              (categories?.length ?? 0) > 0 && (
                <div key={filter} className={styles['filter']} onClick={() => openModal(MODALS.CATEGORIES)}>
                  {t('categories')}
                  {isCategoryFilterApplied && <span className={`mb-4 ${styles.filterCounter}`}>1</span>}
                </div>
              )
            );
          }
          return (
            <div key={filter} className={styles.filter} onClick={() => openFiltersModal(filter)}>
              {t(filter)}
              {getSelectedFilterCount(facets, filter, searchParams) > 0 && (
                <span className={`mb-4 ${styles.filterCounter}`}>
                  {getSelectedFilterCount(facets, filter, searchParams)}
                </span>
              )}
            </div>
          );
        })}
        <div className={styles['allFilters']} onClick={() => openModal(MODALS.FILTERS)}>
          {t('all_filters')}
        </div>
        <CategoriesModal categories={categories} isVisible={activeModal === MODALS.CATEGORIES} onClose={closeModal} />
        <FiltersModal
          counters={counters}
          searchParams={searchParams}
          facets={facets}
          isVisible={activeModal === MODALS.FILTERS}
          onClose={closeModal}
          activeSubFilter={activeSubFilter}
          disabledFilters={disabledFilters}
          //showCategories={openCategories}
          isTopFilter={isTopFilter}
          blackFriday={blackFriday}
        />
      </div>
      <VisibilityTracker eventName={'top_filters_display'} data={null} />
    </>
  );
};

export default TopFilters;
